/* Lumetier domain (lumetiercreatives.com) — front-door route tree.

   The root path serves the Lumetier marketing landing. The Allure
   photography pages (Home, Portfolio, Services, Booking, Contact) are
   intentionally NOT mounted here — they belong to a different brand.

   Shared product routes (/admin, /gallery, /s, /contract, /review) come
   from renderSharedRoutes() so a photographer with an Allure-style site
   running on lumetiercreatives.com can still log into admin from this
   domain — that's the multi-tenant truth of the product. */

import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import LumetierHub from "@/pages/lumetier/Hub";
import LumetierLanding from "@/pages/lumetier/Landing";
import LumetierOnboard from "@/pages/lumetier/Onboard";
import LumetierPreview from "@/pages/LumetierPreview";
import LumetierGalleriesPage from "@/pages/lumetier/products/Galleries";
import LumetierWebsitePage from "@/pages/lumetier/products/Website";
import LumetierManagerPage from "@/pages/lumetier/products/Manager";
import LumetierStorePage from "@/pages/lumetier/products/Store";
import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Portfolio from "@/pages/Portfolio";
import Booking from "@/pages/Booking";
import BookingConfirmation from "@/pages/BookingConfirmation";
import Contact from "@/pages/Contact";
import { TenantProvider } from "@/lib/tenant";
import TenantTemplateShell from "@/templates/TenantTemplateShell";
import { renderSharedRoutes } from "@/routes/sharedRoutes";

/* Allure-only routes ordinarily redirect to / on the Lumetier marketing
   domain. The one exception: when an authenticated admin clicks "New booking"
   from the studio's admin shell, we route to /booking?admin=1 — that's the
   established pattern for creating a booking on behalf of a client. Even if
   the visitor's hostname picked the Lumetier brand, render the Booking page
   so the admin's CTA works. Otherwise fall through to the landing redirect. */
function AdminAwareBooking({ children }) {
  const [params] = useSearchParams();
  if (params.get("admin") === "1") return <Booking />;
  return children;
}

export default function LumetierAppRoutes() {
  return (
    <Routes>
      {/* Front door is the premium product hub. The classic marketing
          landing now lives at /marketing, linked from the hub footer. */}
      <Route path="/" element={<LumetierHub />} />
      <Route path="/marketing" element={<LumetierLanding />} />

      {/* Per-tenant public site — template-aware. Reads
          site_settings.template_id and renders the matching template
          bundle. Today: Editorial. Coming: Minimal, Bold, Journal. */}
      <Route path="/s/:slug/*" element={
        <TenantProvider><TenantTemplateShell /></TenantProvider>
      } />

      {/* Onboarding (invite token from email) */}
      <Route path="/onboard" element={<LumetierOnboard />} />
      <Route path="/lumetier/onboard" element={<LumetierOnboard />} />

      {/* Brand assets / preview page */}
      <Route path="/lumetier-preview" element={<LumetierPreview />} />
      <Route path="/lumetier" element={<Navigate to="/" replace />} />

      {/* Product deep-dive pages (dropdown in nav) */}
      <Route path="/lumetier/galleries" element={<LumetierGalleriesPage />} />
      <Route path="/lumetier/website" element={<LumetierWebsitePage />} />
      <Route path="/lumetier/manager" element={<LumetierManagerPage />} />
      <Route path="/lumetier/store" element={<LumetierStorePage />} />

      {/* Legacy redirects */}
      <Route path="/lumetier/industries" element={<Navigate to="/" replace />} />
      <Route path="/atelier" element={<Navigate to="/" replace />} />
      <Route path="/atelier-preview" element={<LumetierPreview />} />
      <Route path="/atelier/industries" element={<Navigate to="/" replace />} />

      {/* The Allure-only photography routes redirect to the Lumetier landing
          (these URLs shouldn't exist on lumetiercreatives.com). */}
      <Route path="/services" element={<Navigate to="/" replace />} />
      <Route path="/portfolio" element={<Navigate to="/" replace />} />
      <Route path="/booking" element={<AdminAwareBooking><Navigate to="/" replace /></AdminAwareBooking>} />
      <Route path="/contact" element={<Navigate to="/" replace />} />

      {/* Shared product surfaces (admin, public gallery, signing, reviews) */}
      {renderSharedRoutes()}
    </Routes>
  );
}
